import type { FieldCatalogue, RequestPanel } from "@procuremate/shared";
import { clsx } from "clsx";
import { CircleAlert, CircleCheck, LoaderCircle, Send } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import type { Language } from "@/i18n";
import { ApiRequestError } from "@/lib/api-client";
import { useSubmitRequest } from "./api";

/**
 * The panel's fixed bar (WP-2.8): drafts are saved on every change, so it says so, and Send opens a
 * confirmation that lists what would go to procurement as not provided — or what must be answered
 * first. The server's gate decides; the bar only reflects it.
 */
export function SendBar({
  panel,
  catalogue,
  saving,
  suggestSubmit,
  belowContent = false,
}: {
  panel: RequestPanel;
  catalogue: FieldCatalogue | undefined;
  /** A reply is being written or an edit is being saved. */
  saving: boolean;
  /** The last turn's model hint: highlight Send (it never sends). */
  suggestSubmit: boolean;
  /** Wide screens, side by side: the bar sits below the panel's own scroll area instead of over it. */
  belowContent?: boolean;
}) {
  const { t } = useTranslation();
  const [confirming, setConfirming] = useState(false);
  const sendButton = useRef<HTMLButtonElement>(null);
  return (
    <div
      className={clsx(
        "sticky bottom-0 z-10 flex shrink-0 items-center justify-between gap-3 rounded-lg border border-border bg-surface px-4 py-3 shadow-card",
        belowContent && "lg:static",
      )}
    >
      <p aria-live="polite" className="flex min-w-0 items-center gap-1.5 text-sm text-fg-muted">
        {saving ? (
          <LoaderCircle aria-hidden="true" className="h-4 w-4 shrink-0 animate-spin" />
        ) : (
          <CircleCheck aria-hidden="true" className="h-4 w-4 shrink-0 text-success" />
        )}
        <span className="truncate">{saving ? t("requests.panel.bar.saving") : t("requests.panel.bar.saved")}</span>
      </p>
      <Button
        ref={sendButton}
        className={clsx("shrink-0", suggestSubmit && panel.gate.canSubmit && "ring-4 ring-focus/40")}
        disabled={saving}
        onClick={() => setConfirming(true)}
      >
        <Send aria-hidden="true" className="h-4 w-4" />
        {t("requests.panel.bar.send")}
      </Button>
      {confirming ? (
        <SendDialog
          panel={panel}
          catalogue={catalogue}
          onClose={() => {
            setConfirming(false);
            requestAnimationFrame(() => sendButton.current?.focus());
          }}
        />
      ) : null}
    </div>
  );
}

function SendDialog({
  panel,
  catalogue,
  onClose,
}: {
  panel: RequestPanel;
  catalogue: FieldCatalogue | undefined;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const language = i18n.language as Language;
  const navigate = useNavigate();
  const submit = useSubmitRequest(panel.id);
  const dialog = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const { gate } = panel;
  const classified = panel.engagementShape !== null && panel.sizeTier !== null;
  const mustAnswer = gate.openMandatory.filter((k) => !gate.skippable.includes(k));
  const labelOf = (key: string) => catalogue?.fields.find((f) => f.key === key)?.labels[language] ?? key;
  const refused = submit.error instanceof ApiRequestError && submit.error.status === 409;

  useEffect(() => {
    const el = dialog.current;
    if (!el) return;
    // Modal where the browser supports it (focus is kept inside, Escape closes); open otherwise.
    if (typeof el.showModal === "function") el.showModal();
    else el.setAttribute("open", "");
  }, []);

  function send() {
    if (submit.isPending) return;
    submit.mutate(!gate.canSubmit, { onSuccess: () => navigate(`/requests/${panel.id}/submit`) });
  }

  return (
    <dialog
      ref={dialog}
      aria-labelledby={titleId}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      className="m-auto w-[min(32rem,calc(100vw-2rem))] rounded-lg bg-surface p-0 text-fg shadow-card backdrop:bg-fg/40"
    >
      <div className="p-5">
        {gate.canSubmitAnyway ? (
          <>
            <h2 id={titleId} className="font-heading text-lg font-semibold text-primary">
              {t("requests.panel.send.title")}
            </h2>
            <p className="mt-2 text-sm text-fg">{t("requests.panel.send.readyBody", { reference: panel.reference })}</p>
            {!gate.canSubmit ? (
              <div className="mt-3 rounded-md bg-warning-bg p-3 text-sm text-warning-fg">
                <p className="flex items-center gap-1.5 font-semibold">
                  <CircleAlert aria-hidden="true" className="h-4 w-4" />
                  {t("requests.panel.send.missingBody")}
                </p>
                <ul className="mt-1 list-disc pl-5">
                  {gate.skippable.map((k) => (
                    <li key={k}>{labelOf(k)}</li>
                  ))}
                </ul>
              </div>
            ) : null}
          </>
        ) : (
          <>
            <h2 id={titleId} className="font-heading text-lg font-semibold text-danger">
              {t("requests.panel.send.blockedTitle")}
            </h2>
            {classified ? (
              <>
                <p className="mt-2 text-sm text-fg">{t("requests.panel.send.blockedBody")}</p>
                <ul className="mt-1 list-disc pl-5 text-sm">
                  {mustAnswer.map((k) => (
                    <li key={k}>{labelOf(k)}</li>
                  ))}
                </ul>
              </>
            ) : (
              <p className="mt-2 text-sm text-fg">{t("requests.panel.send.unclassifiedBody")}</p>
            )}
          </>
        )}
        {refused ? (
          <p role="alert" className="mt-3 text-sm text-danger">
            {t("requests.submit.refused")}
          </p>
        ) : submit.isError ? (
          <p role="alert" className="mt-3 text-sm text-danger">
            {t("requests.submit.error")}
          </p>
        ) : null}
        <div className="mt-5 flex flex-wrap justify-end gap-2">
          {gate.canSubmitAnyway ? (
            <>
              <Button variant="secondary" onClick={onClose}>
                {t("requests.panel.send.cancel")}
              </Button>
              <Button aria-disabled={submit.isPending || undefined} onClick={send}>
                <Send aria-hidden="true" className="h-4 w-4" />
                {submit.isPending
                  ? t("requests.panel.send.sending")
                  : gate.canSubmit
                    ? t("requests.panel.send.send")
                    : t("requests.panel.send.sendAnyway")}
              </Button>
            </>
          ) : (
            <Button onClick={onClose}>{t("requests.panel.send.close")}</Button>
          )}
        </div>
      </div>
    </dialog>
  );
}
